import React, { useState, useEffect, useRef } from "react";
import { debug } from "../../../utils/debugLogger";
import {
  fetchConversationHistory,
  deleteConversationThread,
  ConversationThread,
} from "../../../services/conversationHistoryService";
import ConfirmDialog from "../../Shared/ConfirmDialog";
import { useChatWorkspace } from "../../../context/ChatWorkspaceContext";

interface ConversationSidebarProps {
  idToken: string;
  customerId: string;
  /** When set (e.g. selected agent in URL), history is fetched for this agent and reloads when it changes. */
  agentId?: string | null;
  currentThreadId?: string | null;
  /** Second arg is the thread's agent when known (for deep-linking to chat). Third arg is the thread's customerId. */
  onThreadSelect: (threadId: string, agentId?: string, customerId?: string) => void;
  onNewChat: () => void;
  className?: string;
  refreshTrigger?: number;
  /** Perplexity-style app shell: hide "Threads" header; parent supplies actions. */
  shellVariant?: boolean;
  /** When provided by the parent (shell variant), filters threads externally. */
  searchQuery?: string;
}

const ConversationSidebar: React.FC<ConversationSidebarProps> = ({
  idToken,
  customerId,
  agentId,
  currentThreadId,
  onThreadSelect,
  onNewChat,
  className = "",
  refreshTrigger,
  shellVariant = false,
  searchQuery: externalSearchQuery,
}) => {
  const [conversations, setConversations] = useState<ConversationThread[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [internalSearchQuery, setInternalSearchQuery] = useState("");
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const hasLoadedOnceRef = useRef(false);

  const { pendingResearchThreadIds, clearResearchPending, pendingImageBatchThreadIds, clearImageBatchPending } = useChatWorkspace();

  const searchQuery = externalSearchQuery ?? internalSearchQuery;

  const confirmingConversation = conversations.find((c) => c.threadId === confirmingId) || null;

  const handleDelete = async (threadId: string) => {
    setDeletingId(threadId);
    setDeleteError(null);
    try {
      await deleteConversationThread(threadId, idToken);
      setConversations((prev) => prev.filter((c) => c.threadId !== threadId));
      // If the open thread was deleted, clear the chat view.
      if (currentThreadId === threadId) {
        onNewChat();
      }
      setConfirmingId(null);
    } catch (err) {
      debug.error("Failed to delete conversation:", err);
      setDeleteError("Failed to delete conversation. Please try again.");
    } finally {
      setDeletingId(null);
    }
  };

  useEffect(() => {
    const loadConversations = async () => {
      if (!idToken || !customerId) {
        return;
      }

      try {
        if (!hasLoadedOnceRef.current) {
          setIsLoading(true);
        }
        setError(null);
        const response = await fetchConversationHistory(
          customerId,
          idToken,
          20,
          undefined,
          agentId || undefined
        );
        setConversations(response.threads);
      } catch (err) {
        debug.error("Failed to load conversations:", err);
        setError("Failed to load conversation history");
      } finally {
        hasLoadedOnceRef.current = true;
        setIsLoading(false);
      }
    };

    loadConversations();
  }, [idToken, customerId, agentId, refreshTrigger]);

  // Reconcile optimistic client-side pending flags with the server: once a
  // refetch reports a thread (other than the open one) as no longer pending, its
  // background research has finished — drop our flag so the dot clears even if
  // the user never reopened that thread. The open thread is owned by the chat
  // window, so skip it here to avoid racing the kickoff write that sets the flag.
  useEffect(() => {
    if (pendingResearchThreadIds.size === 0) return;
    conversations.forEach((conv) => {
      if (
        conv.threadId !== currentThreadId &&
        pendingResearchThreadIds.has(conv.threadId) &&
        !conv.researchPending
      ) {
        clearResearchPending(conv.threadId);
      }
    });
  }, [conversations, currentThreadId, pendingResearchThreadIds, clearResearchPending]);

  // Same reconciliation as above, for a running bulk image batch.
  useEffect(() => {
    if (pendingImageBatchThreadIds.size === 0) return;
    conversations.forEach((conv) => {
      if (
        conv.threadId !== currentThreadId &&
        pendingImageBatchThreadIds.has(conv.threadId) &&
        !conv.imageBatchPending
      ) {
        clearImageBatchPending(conv.threadId);
      }
    });
  }, [conversations, currentThreadId, pendingImageBatchThreadIds, clearImageBatchPending]);

  const filteredConversations = conversations.filter(
    (conv) =>
      conv.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      conv.preview?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div
      className={`flex min-h-0 flex-1 flex-col bg-transparent ${shellVariant ? "" : "bg-theme-background"} ${className}`}
    >
      {!shellVariant && (
        <div className="border-b border-theme-border p-3">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-theme-fg">Threads</h2>
            <button
              type="button"
              onClick={onNewChat}
              className="rounded-md bg-theme-primary px-2.5 py-1.5 text-xs font-medium text-white transition-colors hover:opacity-90"
            >
              New
            </button>
          </div>

          <div className="relative">
            <input
              type="text"
              placeholder="Search…"
              value={internalSearchQuery}
              onChange={(e) => setInternalSearchQuery(e.target.value)}
              className="w-full rounded-md border border-theme-border bg-theme-surface py-2 pl-8 pr-3 text-sm text-theme-fg placeholder:text-theme-fg-muted focus:border-theme-primary focus:outline-none focus:ring-2 focus:ring-theme-primary/30"
            />
            <svg
              className="absolute left-2.5 top-2.5 h-4 w-4 text-theme-fg-muted"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="p-4 text-center text-theme-fg-muted">
            <div className="mx-auto mb-2 h-6 w-6 animate-spin rounded-full border-b-2 border-theme-border"></div>
            <p className="text-sm">
              {shellVariant ? "Loading history…" : "Loading…"}
            </p>
          </div>
        ) : error ? (
          <div className="p-4 text-center text-red-600">
            <p className="text-sm">{error}</p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="mt-2 rounded px-3 py-1 text-xs text-red-700 underline hover:bg-red-50"
            >
              Retry
            </button>
          </div>
        ) : filteredConversations.length === 0 ? (
          <div className="p-4 text-center text-theme-fg-muted">
            <p className="text-sm">
              {searchQuery
                ? "No conversations match your search."
                : "No threads yet."}
            </p>
          </div>
        ) : (
          <div className="py-1">
            {filteredConversations.map((conversation) => {
              // For the open thread the client knows the real research state
              // (the thread is in pendingResearchThreadIds while it runs, removed
              // the instant the report lands), so trust that over the server's
              // now-stale research_pending flag — that's what removes the dot in
              // real time on completion without a refetch. For every other thread
              // (incl. one still researching in the background after we navigated
              // away) the client flag or the server flag keeps the dot lit.
              const isOpenThread = conversation.threadId === currentThreadId;
              const clientResearching = pendingResearchThreadIds.has(
                conversation.threadId
              );
              const showResearchDot =
                clientResearching ||
                (!!conversation.researchPending && !isOpenThread);
              const clientImageBatchRunning = pendingImageBatchThreadIds.has(
                conversation.threadId
              );
              const showImageBatchDot =
                !showResearchDot &&
                (clientImageBatchRunning ||
                  (!!conversation.imageBatchPending && !isOpenThread));
              return (
              <div
                key={conversation.threadId}
                onClick={() =>
                  onThreadSelect(conversation.threadId, conversation.agentId, conversation.customerId)
                }
                className={`group flex cursor-pointer items-center rounded-lg px-3 py-1.5 text-[13px] transition-colors ${
                  currentThreadId === conversation.threadId
                    ? "bg-theme-secondary/15 font-medium text-theme-fg"
                    : "text-theme-fg-muted hover:bg-theme-secondary/10 hover:text-theme-fg"
                }`}
              >
                {showResearchDot && (
                  <span
                    className="mr-2 h-2 w-2 shrink-0 animate-pulse rounded-full bg-[#8B5E34]"
                    title="Deep research still running"
                  />
                )}
                {showImageBatchDot && (
                  <span
                    className="mr-2 h-2 w-2 shrink-0 animate-pulse rounded-full bg-theme-primary"
                    title="Image generation still running"
                  />
                )}
                <span className="flex-1 truncate">
                  {conversation.title || "Untitled"}
                </span>
                <button
                  type="button"
                  title="Delete conversation"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteError(null);
                    setConfirmingId(conversation.threadId);
                  }}
                  className="shrink-0 rounded p-1 text-theme-fg-muted/60 opacity-0 transition-opacity hover:text-red-600 group-hover:opacity-100 focus:opacity-100"
                >
                  <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
              );
            })}
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={!!confirmingId}
        variant="dark"
        title="Delete session?"
        message={
          <>
            <span className="block font-semibold text-white">
              This will permanently delete the session:
            </span>
            <span className="mt-2 block font-semibold text-white">
              {confirmingConversation?.title || "Untitled"}
            </span>
          </>
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        destructive
        isConfirming={deletingId === confirmingId}
        errorMessage={deleteError}
        onConfirm={() => {
          if (confirmingId) handleDelete(confirmingId);
        }}
        onCancel={() => {
          setConfirmingId(null);
          setDeleteError(null);
        }}
      />
    </div>
  );
};

export default ConversationSidebar;
